import { STORAGE_KEYS, type UserRole } from '@/constants/common'
import { useAppStore } from '@/stores'

// --- 1. Token Management ---

/**
 * Helper to set a cookie
 */
const setCookie = (name: string, value: string, days?: number) => {
  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/'
}

/**
 * Helper to get a cookie
 */
const getCookie = (name: string): string | null => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

/**
 * Helper to remove a cookie
 */
const removeCookie = (name: string) => {
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
}

/**
 * Retrieve Access Token from LocalStorage or Cookie.
 * Prioritize LocalStorage (Remember Me) first.
 */
export const getAccessToken = (): string | null => {
  return (
    localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
    getCookie(STORAGE_KEYS.ACCESS_TOKEN) ||
    sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || // Keep as fallback
    null
  )
}

/**
 * Save Access Token.
 * - Remember Me: Save to LocalStorage.
 * - No Remember Me: Save to Session Cookie (clears on browser close).
 */
export const setAccessToken = (token: string, rememberMe: boolean) => {
  if (rememberMe) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token)
    removeCookie(STORAGE_KEYS.ACCESS_TOKEN)
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  } else {
    // Session cookie (no expiration date) -> Cleared when browser closes
    setCookie(STORAGE_KEYS.ACCESS_TOKEN, token)
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  }
}

/**
 * Remove Token (Logout).
 */
export const clearAuthTokens = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  removeCookie(STORAGE_KEYS.ACCESS_TOKEN)
}

// --- 2. User & Role Management (Non-Hook Access) ---
// Used for regular .ts files (like axios interceptors, loaders)
// where React hooks (useAppStore) cannot be used.

/**
 * Get current User directly from Zustand State (without Hook).
 */
export const getCurrentUser = () => {
  return useAppStore.getState().user
}

/**
 * Get current Role.
 */
export const getCurrentRole = (): UserRole | null => {
  const user = useAppStore.getState().user
  return user?.role_name || null
}

/**
 * Check if User is validly logged in.
 * (Exists in Store AND Token exists in Storage).
 */
export const isAuthenticated = (): boolean => {
  const token = getAccessToken()
  const user = getCurrentUser()
  return !!token && !!user
}

import queryClient from '@/configs/reactQuery'

/**
 * Comprehensive Logout function.
 * - Remove token from storage
 * - Clear Zustand store
 * - Clear React Query Cache (Important for staleTime: Infinity)
 * - (Optional) Redirect or hard reload the page
 */
export const logoutApp = () => {
  clearAuthTokens()
  useAppStore.getState().logout()
  queryClient.clear()
}

// --- 3. Role Checking Helpers ---

/**
 * Check if the current Role has access permission.
 */
export const hasPermission = (allowedRoles: UserRole[] | 'ALL'): boolean => {
  if (allowedRoles === 'ALL') return true

  const currentRole = getCurrentRole()
  if (!currentRole) return false

  return allowedRoles.includes(currentRole)
}

// --- 4. Warehouse Management ---

/**
 * Get warehouse_id from Zustand store (without Hook).
 * Used in regular .ts files where React hooks cannot be used.
 */
export const getWarehouseId = (): string | null => {
  return useAppStore.getState().warehouseId
}

/**
 * Set warehouse_id in Zustand store.
 * Typically called once during app initialization.
 */
export const setWarehouseId = (id: string | null) => {
  useAppStore.getState().setWarehouseId(id)
}

// get distributor name
export const getDistributorName = (): string => {
  return useAppStore.getState().user?.organization_name || ''
}
